import {useCallback, useEffect, useMemo, useRef} from 'react';

import {type BrowseCourse} from '@udemy/browse-course';
import {
    AGGREGATION_TYPE_PRICE,
    PAGE_TYPE_ORG_TOPIC,
    PAGE_TYPE_SUBS_TOPIC,
    PAGE_TYPE_TOPIC,
} from '@udemy/browse-utils';
import {DiscoveryUnit} from '@udemy/discovery-api';
import {Tracker} from '@udemy/event-tracking';
import {useMatchMedia} from '@udemy/hooks';
import {ExperimentImpressionEvent} from '@udemy/react-experimentation-provider';
import {serverOrClient} from '@udemy/shared-utils';
import {CUSTOM_EVENT_NAME_PROVIDER_BRIDGE} from '@udemy/ud-data';

import {PRESET_FREE, SPECIAL_PRICING_TOPIC_IDS_BY_LOCALE} from '../constants';
import {TopicPageViewEvent} from '../events';
import {isOrgTopicPage, isSubsTopicPage, TopicPageType} from '../types/page';
import {type BundleVariant, BUNDLE_VARIANT} from '../types/topic';
import {getPageObjectIdFromTopic} from '../utils';
import {useTopicRecommendations} from './use-topic-recommendations';

import type {TopicProps} from '../topic-recommendations.react-component';

const getBundleVariant = ({
    isMXUser,
    certificationBundles,
    genericBundleCourses,
    topicName,
    showSpecialBundlePricing,
    showCertificationVoucherBundleUnit,
}: {
    isMXUser: boolean;
    certificationBundles: TopicProps['certificationBundles'];
    genericBundleCourses: BrowseCourse[] | undefined;
    topicName: string;
    showSpecialBundlePricing: boolean;
    showCertificationVoucherBundleUnit: boolean;
}): BundleVariant | null => {
    const showCertBundle = isMXUser && certificationBundles.length > 0;

    if (showCertBundle) {
        return {
            type: BUNDLE_VARIANT.CERTIFICATION,
            certificationBundles,
            showCertificationVoucherBundleUnit,
        };
    }
    if (isMXUser && genericBundleCourses) {
        return {
            type: BUNDLE_VARIANT.GENERIC,
            courses: genericBundleCourses,
            topicName,
            enableSpecialBundlePricing: showSpecialBundlePricing,
        };
    }
    if (isMXUser) {
        return {type: BUNDLE_VARIANT.VOUCHER_ONLY};
    }
    return null;
};

export const useTopicPageData = (props: TopicProps) => {
    const {
        udData,
        isConsumerSubsSubscriber,
        isSubsLoading,
        certification,
        certificationBundles,
        tertiaryDescription,
        enrollmentStat,
        locale,
        topic,
        presetValue,
        showTopicSpecialBundlePricing,
        showCertificationTopicHeaderFeature,
        certificationVoucherBundleUnitFeature,
        enableLabsInPersonalPlan,
        enableAssessmentsInPersonalPlan,
        primaryDescription,
        questionsAndAnswers,
        relatedPages,
    } = props;
    const {Config, request, me} = udData;
    const marketplaceCountryId = Config.marketplace_country?.id;

    const mobileMaxMedia = useMatchMedia('mobile-max');
    const isMobile = mobileMaxMedia != null ? mobileMaxMedia : Boolean(request?.isMobile);

    const pageType: TopicPageType = Config.brand.has_organization
        ? PAGE_TYPE_ORG_TOPIC
        : isConsumerSubsSubscriber
          ? PAGE_TYPE_SUBS_TOPIC
          : PAGE_TYPE_TOPIC;

    const {
        units: recommendationUnits,
        bundleCourses: genericBundleCourses,
        relatedCategories,
        isLoading: isRecommendationsLoading,
        onLoadMore,
    } = useTopicRecommendations({
        topicId: topic.id,
        pageType: pageType,
        shouldFetchBundle: certificationBundles.length < 1,
    });

    const discoveryListRef = useRef<HTMLDivElement>(null);
    const hasPublishedSpecialBundlePricingImpressionEvent = useRef(false);
    const hasPublishedCertificationTopicHeaderImpressionEvent = useRef(false);

    const isMXUser = !isConsumerSubsSubscriber && !Config.brand.has_organization;
    const isUserDataLoading = isSubsLoading || udData.isGlobalMeContextLoading;

    const showCertificationVoucherBundleUnit =
        certificationVoucherBundleUnitFeature?.configuration?.showCertificationVoucherBundleUnit ??
        false;

    const bundleVariant = getBundleVariant({
        isMXUser,
        certificationBundles,
        genericBundleCourses,
        topicName: topic.name,
        showSpecialBundlePricing:
            showTopicSpecialBundlePricing?.configuration?.enableSpecialBundlePricing ?? false,
        showCertificationVoucherBundleUnit,
    });

    const localizedEnrollmentStat = useMemo(() => {
        if (!enrollmentStat) return enrollmentStat;
        const cleaned = enrollmentStat.replace(/\D/g, '');
        try {
            return parseInt(cleaned).toLocaleString(locale.replace('_', '-') || 'en-US');
        } catch {
            return enrollmentStat;
        }
    }, [enrollmentStat, locale]);

    const hasReplacementTitle = !!certification || !!tertiaryDescription;
    const isTopicDataLoading = isUserDataLoading || isRecommendationsLoading;

    const showCertificationUnit = isSubsTopicPage(pageType) || isOrgTopicPage(pageType);

    const showLearnMoreSection =
        primaryDescription || relatedPages.length > 1 || questionsAndAnswers.length > 0;

    const presetFilters =
        presetValue === PRESET_FREE ? {[AGGREGATION_TYPE_PRICE]: ['price-free']} : undefined;

    const isProPath = Config.features.organization?.learning_path?.pro_path ?? false;

    const discoveryUnitFilter = useCallback(
        (unit: Partial<DiscoveryUnit>) => {
            const isUBUser = me.is_authenticated && 'organization' in me && !!me.organization;
            const isUBProUser =
                isUBUser &&
                (isProPath || ('organization' in me && me.organization?.is_pro_license_holder));

            const shouldShowLabs =
                (isSubsTopicPage(pageType) && enableLabsInPersonalPlan) || isUBProUser;
            const shouldShowAssessments =
                (isSubsTopicPage(pageType) && enableAssessmentsInPersonalPlan) || isUBProUser;

            if (presetValue === PRESET_FREE) {
                return unit.type === 'bestseller_labels';
            }
            if (
                unit.type === 'related_categories_and_subcategories' ||
                unit.type === 'category' ||
                unit.type === 'subcategory'
            ) {
                return false;
            }
            if (!shouldShowLabs) {
                return unit?.item_type !== 'lab';
            }
            if (!shouldShowAssessments) {
                return unit?.item_type !== 'assessment';
            }
            return true;
        },
        [
            pageType,
            presetValue,
            enableLabsInPersonalPlan,
            enableAssessmentsInPersonalPlan,
            me,
            isProPath,
        ],
    );

    const handlePageChange = useCallback(() => {
        if (serverOrClient.global.scrollY && discoveryListRef?.current) {
            const y =
                discoveryListRef.current.getBoundingClientRect().top +
                serverOrClient.global.scrollY;
            serverOrClient.global.scrollTo?.({top: y, behavior: 'smooth'});
        }
    }, []);

    useEffect(() => {
        // Fire once on mount to track the initial page view
        Tracker.publishEvent(new TopicPageViewEvent({topicId: getPageObjectIdFromTopic(topic)}));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (topic.id && marketplaceCountryId) {
            setTimeout(() => {
                window.dispatchEvent(
                    new CustomEvent(CUSTOM_EVENT_NAME_PROVIDER_BRIDGE, {
                        detail: {
                            marketplace_country: {},
                            pageContext: {entityId: topic.id.toString()},
                        },
                    }),
                );
            }, 0);
        }
    }, [topic.id, marketplaceCountryId]);

    useEffect(() => {
        if (hasPublishedSpecialBundlePricingImpressionEvent.current) return;
        if (!genericBundleCourses) return;

        const specialPricingTopicIds = SPECIAL_PRICING_TOPIC_IDS_BY_LOCALE[request.locale];
        const topicId = Number(topic.id);

        if (specialPricingTopicIds?.includes(topicId) && showTopicSpecialBundlePricing) {
            Tracker.publishEvent(
                new ExperimentImpressionEvent(
                    showTopicSpecialBundlePricing.experimentIds,
                    showTopicSpecialBundlePricing.isLocalDefault,
                ),
            );
            hasPublishedSpecialBundlePricingImpressionEvent.current = true;
        }
    }, [genericBundleCourses, showTopicSpecialBundlePricing, topic.id, request.locale]);

    useEffect(() => {
        if (hasPublishedCertificationTopicHeaderImpressionEvent.current) return;
        if (!certification || !showCertificationTopicHeaderFeature) return;

        Tracker.publishEvent(
            new ExperimentImpressionEvent(
                showCertificationTopicHeaderFeature.experimentIds,
                showCertificationTopicHeaderFeature.isLocalDefault,
            ),
        );
        hasPublishedCertificationTopicHeaderImpressionEvent.current = true;
    }, [certification, showCertificationTopicHeaderFeature]);

    return {
        bundleVariant,
        discoveryListRef,
        hasReplacementTitle,
        isMobile,
        isTopicDataLoading,
        isUserDataLoading,
        localizedEnrollmentStat,
        pageType,
        presetFilters,
        recommendationUnits,
        relatedCategories,
        showCertificationUnit,
        showLearnMoreSection,
        discoveryUnitFilter,
        handlePageChange,
        onLoadMore,
    };
};
